import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { on as onEvent } from '@/utils/eventBus';
import {
  Home, BookOpen, Music, Activity, Heart, Calculator, Pencil,
  GraduationCap, Plus, Play, Calendar, Target, ChevronRight,
  Loader2, Award, Clock, Users, Settings, LogOut, BarChart3
} from 'lucide-react';

const HomeschoolDashboard = () => {
  const { api, user, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [selectedChild, setSelectedChild] = useState(null);
  const [lessonPlan, setLessonPlan] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  const subjects = [
    { id: 'reading', name: 'Reading/ELA', icon: BookOpen, color: 'bg-blue-500', curriculum: 'EL Education' },
    { id: 'math', name: 'Math', icon: Calculator, color: 'bg-green-500', curriculum: 'Envision' },
    { id: 'music', name: 'Music', icon: Music, color: 'bg-purple-500', curriculum: 'Music Theory & Practice' },
    { id: 'pe', name: 'PE', icon: Activity, color: 'bg-orange-500', curriculum: 'Physical Education' },
    { id: 'science', name: 'Science', icon: GraduationCap, color: 'bg-teal-500' },
    { id: 'writing', name: 'Writing', icon: Pencil, color: 'bg-pink-500' },
    { id: 'christian', name: 'Bible', icon: Heart, color: 'bg-amber-500' },
  ];

  useEffect(() => {
    fetchDashboardData();
  }, []);

  // Refresh when the child record changes elsewhere (e.g. Sight Words saves grade)
  // or after an assessment writes new Lexile/composite scores.
  useEffect(() => {
    const offChild = onEvent('child:updated', () => fetchDashboardData());
    const offAssess = onEvent('assessment:completed', () => fetchDashboardData());
    return () => { offChild(); offAssess(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedChild) {
      fetchLessonPlan(selectedChild);
    }
  }, [selectedChild]);

  const fetchDashboardData = async () => {
    try {
      const response = await api.get('/homeschool/dashboard');
      setDashboardData(response.data);
      if (response.data.children.length > 0) {
        setSelectedChild(response.data.children[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
      if (error.response?.status === 403) {
        toast.error('Homeschool account required');
        navigate('/dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchLessonPlan = async (childId) => {
    try {
      const response = await api.get(`/homeschool/lesson-plan/${childId}`);
      setLessonPlan(response.data);
    } catch (error) {
      console.error('Failed to fetch lesson plan:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-amber-50 to-white">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  const currentChild = dashboardData?.children.find(c => c.id === selectedChild);

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white">
      {/* Header */}
      <header className="bg-white border-b border-amber-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
                <Home className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="font-bold text-xl text-slate-800">Homeschool Dashboard</h1>
                <p className="text-sm text-slate-500">Welcome, {dashboardData?.user?.name}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Link to="/child/new">
                <Button variant="outline" size="sm" data-testid="add-child-btn">
                  <Plus className="w-4 h-4 mr-1" /> Add Child
                </Button>
              </Link>
              <Link to="/settings">
                <Button variant="ghost" size="icon">
                  <Settings className="w-5 h-5" />
                </Button>
              </Link>
              <Button variant="ghost" size="icon" onClick={handleLogout}>
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Child Selector */}
        {dashboardData?.children.length > 0 && (
          <div className="flex gap-3 mb-8 overflow-x-auto pb-2">
            {dashboardData.children.map(child => (
              <button
                key={child.id}
                onClick={() => setSelectedChild(child.id)}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all whitespace-nowrap ${
                  selectedChild === child.id 
                    ? 'bg-amber-500 text-white shadow-lg' 
                    : 'bg-white border hover:border-amber-300'
                }`}
                data-testid={`child-selector-${child.id}`}
              >
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold ${
                  selectedChild === child.id ? 'bg-white/20' : 'bg-amber-100 text-amber-600'
                }`}>
                  {child.name[0]}
                </div>
                <div className="text-left">
                  <p className="font-medium">{child.name}</p>
                  <p className={`text-xs ${selectedChild === child.id ? 'text-amber-100' : 'text-slate-500'}`}>
                    {child.grade_level || child.age_group || 'Level ' + (child.current_level || 1)}
                  </p>
                </div>
              </button>
            ))}
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="subjects">Subjects</TabsTrigger>
            <TabsTrigger value="lesson-plan">Lesson Plan</TabsTrigger>
            <TabsTrigger value="progress">Progress</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Quick Stats */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-amber-500" />
                    Today's Progress
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid sm:grid-cols-3 gap-4 mb-6">
                    <div className="p-4 bg-blue-50 rounded-xl text-center">
                      <p className="text-2xl font-bold text-blue-600">{currentChild?.total_activities_completed || 0}</p>
                      <p className="text-sm text-blue-700">Activities Done</p>
                    </div>
                    <div className="p-4 bg-green-50 rounded-xl text-center">
                      <p className="text-2xl font-bold text-green-600">{currentChild?.total_xp || 0}</p>
                      <p className="text-sm text-green-700">XP Earned</p>
                    </div>
                    <div className="p-4 bg-purple-50 rounded-xl text-center">
                      <p className="text-2xl font-bold text-purple-600">{currentChild?.daily_streak || 0}</p>
                      <p className="text-sm text-purple-700">Day Streak</p>
                    </div>
                  </div>

                  {/* Reading Scores */}
                  {currentChild && (
                    <div className="border-t pt-4">
                      <h4 className="font-medium mb-3">Reading Assessment Scores</h4>
                      <div className="grid sm:grid-cols-4 gap-3">
                        <Link to={`/assessment/${currentChild.id}/reading`} className="block">
                          <div className="p-3 bg-slate-50 rounded-lg hover:bg-slate-100 cursor-pointer transition-all">
                            <p className="text-sm text-slate-500">Lexile</p>
                            <p className="text-xl font-bold text-slate-700">
                              {currentChild.lexile_score ? `${currentChild.lexile_score}L` : '--'}
                            </p>
                          </div>
                        </Link>
                        <Link to={`/assessment/${currentChild.id}/reading`} className="block">
                          <div className="p-3 bg-slate-50 rounded-lg hover:bg-slate-100 cursor-pointer transition-all">
                            <p className="text-sm text-slate-500">Composite</p>
                            <p className="text-xl font-bold text-slate-700">
                              {currentChild.reading_composite_score || '--'}
                            </p>
                          </div>
                        </Link>
                        <Link to={`/sight-words/${currentChild.id}`} className="block">
                          <div className="p-3 bg-slate-50 rounded-lg hover:bg-slate-100 cursor-pointer transition-all">
                            <p className="text-sm text-slate-500">Sight Words</p>
                            <p className="text-xl font-bold text-slate-700">
                              {currentChild.sight_words_mastered?.length || 0}
                            </p>
                          </div>
                        </Link>
                        <Link to={`/timed-reading/${currentChild.id}`} className="block">
                          <div className="p-3 bg-slate-50 rounded-lg hover:bg-slate-100 cursor-pointer transition-all">
                            <p className="text-sm text-slate-500">WPM</p>
                            <p className="text-xl font-bold text-slate-700">
                              {currentChild.timed_reading_wpm || '--'}
                            </p>
                          </div>
                        </Link>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Quick Actions */}
              <Card>
                <CardHeader>
                  <CardTitle>Quick Actions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Link to={currentChild ? `/learn/${currentChild.id}` : '#'} className="block">
                    <Button className="w-full justify-start bg-amber-500 hover:bg-amber-600" disabled={!currentChild}>
                      <Play className="w-4 h-4 mr-2" /> Start Learning
                    </Button>
                  </Link>
                  <Link to={currentChild ? `/sight-words/${currentChild.id}` : '#'} className="block">
                    <Button variant="outline" className="w-full justify-start" disabled={!currentChild}>
                      <BookOpen className="w-4 h-4 mr-2" /> Daily Sight Words
                    </Button>
                  </Link>
                  <Link to={currentChild ? `/timed-reading/${currentChild.id}` : '#'} className="block">
                    <Button variant="outline" className="w-full justify-start" disabled={!currentChild}>
                      <Clock className="w-4 h-4 mr-2" /> Timed Reading
                    </Button>
                  </Link>
                  <Link to={currentChild ? `/badges/${currentChild.id}` : '#'} className="block">
                    <Button variant="outline" className="w-full justify-start" disabled={!currentChild}>
                      <Award className="w-4 h-4 mr-2" /> View Badges
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Subjects Tab */}
          <TabsContent value="subjects">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {subjects.map(subject => {
                const progress = currentChild?.subjects_progress?.[subject.id] || 0;
                return (
                  <Link 
                    key={subject.id} 
                    to={currentChild ? `/learn/${currentChild.id}?subject=${subject.id}` : '#'}
                    className="block"
                  >
                    <Card className="hover:shadow-lg transition-all cursor-pointer" data-testid={`subject-card-${subject.id}`}>
                      <CardContent className="p-6">
                        <div className="flex items-center gap-4 mb-4">
                          <div className={`w-12 h-12 ${subject.color} rounded-xl flex items-center justify-center`}>
                            <subject.icon className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-lg">{subject.name}</h3>
                            {subject.curriculum && (
                              <p className="text-xs text-slate-500">{subject.curriculum}</p>
                            )}
                          </div>
                        </div>
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span>Progress</span>
                            <span className="font-medium">{progress} completed</span>
                          </div>
                          <Progress value={Math.min(progress * 10, 100)} className="h-2" />
                        </div>
                        <div className="mt-4 flex justify-end">
                          <ChevronRight className="w-5 h-5 text-slate-400" />
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>
          </TabsContent>

          {/* Lesson Plan Tab */}
          <TabsContent value="lesson-plan">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-amber-500" />
                  Today's Lesson Plan for {lessonPlan?.child_name}
                </CardTitle>
                <CardDescription>
                  Personalized curriculum for {lessonPlan?.grade_level || 'your child'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {lessonPlan?.subjects && Object.keys(lessonPlan.subjects).length > 0 ? (
                  <div className="space-y-6">
                    {Object.entries(lessonPlan.subjects).map(([subject, activities]) => {
                      const subjectInfo = subjects.find(s => s.id === subject);
                      if (!activities || activities.length === 0) return null;
                      return (
                        <div key={subject} className="border rounded-lg p-4">
                          <div className="flex items-center gap-3 mb-3">
                            {subjectInfo && (
                              <div className={`w-8 h-8 ${subjectInfo.color} rounded-lg flex items-center justify-center`}>
                                <subjectInfo.icon className="w-4 h-4 text-white" />
                              </div>
                            )}
                            <h4 className="font-semibold capitalize">{subject}</h4>
                            <span className="text-sm text-slate-500">({activities.length} activities)</span>
                          </div>
                          <div className="space-y-2 ml-11">
                            {activities.slice(0, 5).map((activity, idx) => (
                              <Link 
                                key={activity.id}
                                to={`/activity/${activity.id}?child=${selectedChild}`}
                                className="block"
                              >
                                <div className="flex items-center justify-between p-2 hover:bg-slate-50 rounded-lg cursor-pointer transition-all">
                                  <div className="flex items-center gap-3">
                                    <span className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center text-sm">
                                      {idx + 1}
                                    </span>
                                    <div>
                                      <p className="font-medium text-sm">{activity.title}</p>
                                      <p className="text-xs text-slate-500">{activity.estimated_time} min | {activity.xp_reward} XP</p>
                                    </div>
                                  </div>
                                  <Play className="w-4 h-4 text-slate-400" />
                                </div>
                              </Link>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No lesson plan available</p>
                    <p className="text-sm text-slate-400">Add activities to your curriculum to see a lesson plan</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Progress Tab */}
          <TabsContent value="progress">
            <div className="grid lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Subject Progress</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {subjects.map(subject => {
                    const progress = currentChild?.subjects_progress?.[subject.id] || 0;
                    return (
                      <div key={subject.id} className="flex items-center gap-4">
                        <div className={`w-10 h-10 ${subject.color} rounded-lg flex items-center justify-center flex-shrink-0`}>
                          <subject.icon className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1">
                          <div className="flex justify-between mb-1">
                            <span className="text-sm font-medium">{subject.name}</span>
                            <span className="text-sm text-slate-500">{progress} activities</span>
                          </div>
                          <Progress value={Math.min(progress * 10, 100)} className="h-2" />
                        </div>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Achievements</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4">
                    <Link to={currentChild ? `/badges/${currentChild.id}` : '#'} className="block">
                      <div className="text-center p-4 bg-amber-50 rounded-xl cursor-pointer hover:bg-amber-100 transition-all">
                        <Award className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                        <p className="text-2xl font-bold text-amber-700">
                          {currentChild?.badges?.length || 0}
                        </p>
                        <p className="text-xs text-amber-600">Badges</p>
                      </div>
                    </Link>
                    <div className="text-center p-4 bg-blue-50 rounded-xl">
                      <Target className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-blue-700">
                        Level {currentChild?.current_level || 1}
                      </p>
                      <p className="text-xs text-blue-600">Current</p>
                    </div>
                    <div className="text-center p-4 bg-green-50 rounded-xl">
                      <Clock className="w-8 h-8 text-green-500 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-green-700">
                        {Math.round((currentChild?.total_time_spent || 0) / 60)}
                      </p>
                      <p className="text-xs text-green-600">Minutes</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>

        {/* No Children Message */}
        {dashboardData?.children.length === 0 && (
          <Card className="text-center py-12">
            <CardContent>
              <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="font-semibold text-lg mb-2">No Children Added Yet</h3>
              <p className="text-slate-500 mb-4">Add your first child to start homeschooling!</p>
              <Link to="/child/new">
                <Button className="bg-amber-500 hover:bg-amber-600">
                  <Plus className="w-4 h-4 mr-2" /> Add Your First Child
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default HomeschoolDashboard;
